import React, { useEffect, useState } from "react";
import { GraduationCap, User as UserIcon, Play } from "lucide-react";
import api from "../api";

const VIDEOS = [
  {
    audience: "client",
    file: "client_spot.mp4",
    duration: "8s",
    title: "Trouvez votre maître-nageur",
    desc: "Un teaser pour découvrir l'app côté client en quelques secondes.",
  },
  {
    audience: "client",
    file: "client_explainer.mp4",
    duration: "12s",
    title: "Réservez votre cours en 1 clic",
    desc: "De la recherche au paiement : tout le parcours client en images.",
  },
  {
    audience: "mns",
    file: "mns_spot.mp4",
    duration: "8s",
    title: "Votre activité simplifiée",
    desc: "Spot dédié aux maîtres-nageurs : planning, paiements, statistiques.",
  },
  {
    audience: "mns",
    file: "mns_explainer.mp4",
    duration: "12s",
    title: "Pilotez votre studio depuis votre poche",
    desc: "Le quotidien d'un MNS sur MySwimmingLessons.",
  },
];

function VideoCard({ v, available, baseUrl }) {
  const [playing, setPlaying] = useState(false);
  const url = `${baseUrl}/api/marketing/videos/${v.file}`;
  const Icon = v.audience === "mns" ? GraduationCap : UserIcon;
  const tone = v.audience === "mns" ? "from-teal-100 to-sky-100 text-teal-700" : "from-sky-100 to-cyan-100 text-sky-700";
  const audienceLabel = v.audience === "mns" ? "Maîtres-Nageurs" : "Clients";

  return (
    <div className="card-soft overflow-hidden flex flex-col" data-testid={`video-card-${v.file}`}>
      <div className="relative aspect-video bg-slate-900 group">
        {available ? (
          playing ? (
            <video
              src={url}
              controls
              autoPlay
              className="w-full h-full object-cover"
              data-testid={`video-${v.file}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="w-full h-full relative"
              aria-label={`Lire la vidéo ${v.title}`}
              data-testid={`play-${v.file}`}
            >
              <video
                src={url}
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white/90 text-sky-700 rounded-full p-4 shadow-2xl group-hover:scale-110 transition">
                  <Play className="w-6 h-6 fill-sky-700" />
                </span>
              </span>
              <span className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{v.duration}</span>
            </button>
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-sky-900 text-slate-300">
            <div className="text-center px-4">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-amber-500/20 text-amber-200 rounded-full px-3 py-1 mb-3">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Bientôt disponible
              </div>
              <p className="text-sm text-slate-300/90">Cette vidéo sera générée prochainement.</p>
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold bg-gradient-to-r ${tone} px-2.5 py-1 rounded-full`}>
          <Icon className="w-3.5 h-3.5" /> {audienceLabel}
        </div>
        <h3 className="font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{v.title}</h3>
        <p className="text-sm text-slate-600 mt-1">{v.desc}</p>
      </div>
    </div>
  );
}

export default function HowItWorks() {
  const [available, setAvailable] = useState(new Set());
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    api.get("/marketing/videos").then((r) => setAvailable(new Set(r.data.videos || []))).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16" data-testid="how-it-works">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-teal-700 bg-teal-100 px-3 py-1 rounded-full">
          Comment ça marche ?
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4" style={{ fontFamily: "Outfit" }}>
          Découvrez MySwimmingLessons en vidéo.
        </h2>
        <p className="text-slate-600 mt-3">
          Quatre courtes vidéos pour comprendre l'app — deux pour les <strong>clients</strong>, deux pour les <strong>maîtres-nageurs</strong>.
        </p>
      </div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {VIDEOS.map((v) => (
          <VideoCard key={v.file} v={v} available={available.has(v.file)} baseUrl={baseUrl} />
        ))}
      </div>
    </section>
  );
}
